import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { ASSETS } from "@/lib/assets";
import { Check, X, Sun, Hand, Camera, Focus, ArrowRight } from "lucide-react";

const stepIcons = [Hand, Sun, Camera, Focus];

export default function Instructions() {
  const { t, user } = useApp();
  const navigate = useNavigate();

  return (
    <div className="mx-auto max-w-6xl px-6 py-14">
      <div className="text-center mb-12 rise-in">
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-amber-50">{t.instructions.title}</h1>
        <p className="mt-4 text-slate-300 max-w-2xl mx-auto">{t.instructions.subtitle}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <div className="space-y-4">
          {t.instructions.steps.map((s, i) => {
            const Icon = stepIcons[i];
            return (
              <div key={i} className="glass rounded-2xl p-5 flex gap-4" data-testid={`instruction-step-${i}`}>
                <div className="h-11 w-11 shrink-0 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/20 to-purple-600/20 border border-amber-500/30">
                  <Icon className="text-amber-300" size={20} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-amber-100">{s.t}</h3>
                  <p className="text-sm text-slate-400 mt-1">{s.d}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="relative">
          <div className="absolute -inset-4 rounded-3xl bg-purple-600/15 blur-2xl" />
          <div className="relative rounded-3xl overflow-hidden border border-amber-500/25 gold-glow">
            <img src={ASSETS.goodExample} alt={t.instructions.goodTitle} className="w-full max-h-[60vh] object-cover" />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <span className="inline-flex items-center gap-2 text-sm text-emerald-300">
                <Check size={16} /> {t.instructions.goodTitle}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Do / Don't */}
      <div className="grid sm:grid-cols-2 gap-6 mt-12">
        <div className="glass rounded-2xl p-6 border border-emerald-500/25">
          <h3 className="font-serif text-emerald-300 mb-4 flex items-center gap-2"><Check size={18} /> Do</h3>
          <ul className="space-y-2">
            {t.instructions.dos.map((d, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                <Check size={15} className="text-emerald-400" /> {d}
              </li>
            ))}
          </ul>
        </div>
        <div className="glass rounded-2xl p-6 border border-rose-500/25">
          <h3 className="font-serif text-rose-300 mb-4 flex items-center gap-2"><X size={18} /> Don't</h3>
          <ul className="space-y-2">
            {t.instructions.donts.map((d, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                <X size={15} className="text-rose-400" /> {d}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="text-center mt-12">
        <button
          onClick={() => navigate(user ? "/upload" : "/login")}
          data-testid="instructions-cta-btn"
          className="btn-gold rounded-full px-8 py-3.5 inline-flex items-center gap-2 gold-glow"
        >
          {t.instructions.cta} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
